import { FormattedMessage } from 'react-intl';

import { LimitsDetails } from '@clients/accountsClient';
import LimitPiePanel from '@components/overviewPanels/limitPiePanel/LimitPiePanel';

interface LimitPieFactoryProps {
    isLoadingLimits: boolean;
    limits: LimitsDetails;
    type: 'MonthlyCreditLimit' | 'WithdrawalLimit' | 'InstallmentCreditLimit';
}

const limitSpent = (total: number, available: number) => {
    if (typeof total !== 'number' || typeof available !== 'number') return 0;
    const limit = total - available;
    if (!limit || limit < 0) return 0;
    return limit;
};

const LimitPieFactory = ({
    isLoadingLimits,
    limits,
    type,
}: LimitPieFactoryProps) => {
    const usedText = (
        <FormattedMessage id="LimitPieFactory.used" defaultMessage="Used" />
    );
    const availableText = (
        <FormattedMessage
            id="LimitPieFactory.available"
            defaultMessage="Available"
        />
    );
    const limitText = (
        <FormattedMessage id="LimitPieFactory.limit" defaultMessage="Limit" />
    );
    switch (type) {
        case 'MonthlyCreditLimit':
            return (
                <LimitPiePanel
                    title={
                        <FormattedMessage
                            id="LimitPieFactory.monthlyCredit"
                            defaultMessage="Monthly credit"
                        />
                    }
                    legendValuesNotPlotted={[
                        {
                            value: limits?.monthly_credit_limit,
                            name: limitText,
                            id: 'limit',
                        },
                    ]}
                    limitsToPlot={[
                        {
                            value: limitSpent(
                                limits?.monthly_credit_limit,
                                limits?.available_monthly_credit,
                            ),
                            name: usedText,
                            id: 'used',
                        },
                        {
                            value: limits?.available_monthly_credit,
                            name: availableText,
                            id: 'available',
                        },
                    ]}
                    isLoadingLimits={isLoadingLimits}
                />
            );
        case 'InstallmentCreditLimit':
            return (
                <LimitPiePanel
                    title={
                        <FormattedMessage
                            id="LimitPieFactory.installmentCredit"
                            defaultMessage="Installment credit"
                        />
                    }
                    legendValuesNotPlotted={[
                        {
                            value: limits?.total_installment_credit_limit,
                            name: limitText,
                            id: 'limit',
                        },
                    ]}
                    limitsToPlot={[
                        {
                            value: limitSpent(
                                limits?.total_installment_credit_limit,
                                limits?.available_total_installment_credit,
                            ),
                            name: usedText,
                            id: 'used',
                        },
                        {
                            value: limits?.available_total_installment_credit,
                            name: availableText,
                            id: 'available',
                        },
                    ]}
                    isLoadingLimits={isLoadingLimits}
                />
            );
        case 'WithdrawalLimit':
        default:
            return (
                <LimitPiePanel
                    title={
                        <FormattedMessage
                            id="LimitPieFactory.withdrawalLimit"
                            defaultMessage="Withdrawal limit"
                        />
                    }
                    legendValuesNotPlotted={[
                        {
                            value: limits?.withdrawal_credit_limit,
                            name: limitText,
                            id: 'limit',
                        },
                    ]}
                    limitsToPlot={[
                        {
                            value: limitSpent(
                                limits?.withdrawal_credit_limit,
                                limits?.available_withdrawal_credit,
                            ),
                            name: usedText,
                            id: 'used',
                        },
                        {
                            value: limits?.available_withdrawal_credit,
                            name: availableText,
                            id: 'available',
                        },
                    ]}
                    isLoadingLimits={isLoadingLimits}
                />
            );
    }
};
export default LimitPieFactory;
